import React, { createContext, CSSProperties, HTMLAttributes, ReactNode, useContext } from "react";
import { MGTextColors, TMGColorBackgrounds, TMGColorTexts } from "../../colors";
import styles from "./MGTypography.module.css";

const BackgroundContext = createContext<TMGColorBackgrounds>("light");

export const MGTypographyProvider = ({
  background,
  children,
}: {
  background: TMGColorBackgrounds;
  children: ReactNode;
}) => <BackgroundContext.Provider value={background}>{children}</BackgroundContext.Provider>;

export type TMGTypographyProps = Omit<HTMLAttributes<HTMLElement>, "color"> & {
  children: ReactNode;
  background?: TMGColorBackgrounds;
  color?: TMGColorTexts;
  tagName?: keyof HTMLElementTagNameMap;
  htmlFor?: string;
  dataTestId?: string;
};

type TTypographyDefinition = {
  fontFamily: string;
  fontSize: string;
  fontWeight: number;
  lineHeight: number;
  textTransform: CSSProperties["textTransform"];
};

type TTypographyStyle = CSSProperties & {
  "--mg-type-font": string;
  "--mg-type-size": string;
  "--mg-type-weight": number;
  "--mg-type-line-height": number;
  "--mg-type-case": CSSProperties["textTransform"];
  "--mg-type-color": string;
};

export const MGTypography = ({
  definition,
  componentName,
  background,
  color,
  tagName = "div",
  className,
  style,
  dataTestId,
  children,
  ...htmlProps
}: TMGTypographyProps & {
  definition: TTypographyDefinition | undefined;
  componentName: string;
}) => {
  const inheritedBackground = useContext(BackgroundContext);
  const resolvedBackground = background === undefined ? inheritedBackground : background;
  const resolvedColor =
    color || (resolvedBackground === "dark" ? "text-primary-04" : "text-primary-01");
  const typographyStyle: TTypographyStyle | undefined = definition
    ? {
        "--mg-type-font": definition.fontFamily,
        "--mg-type-size": definition.fontSize,
        "--mg-type-weight": definition.fontWeight,
        "--mg-type-line-height": definition.lineHeight,
        "--mg-type-case": definition.textTransform,
        "--mg-type-color": MGTextColors[resolvedColor],
      }
    : undefined;

  return React.createElement(
    tagName,
    {
      ...htmlProps,
      className: `${styles.typography} ${componentName}${className ? ` ${className}` : ""}`,
      style: { ...typographyStyle, ...style },
      "data-testid": dataTestId,
    },
    children,
  );
};
